import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import JobApplicationForm from "@/components/JobApplicationForm";
import SkylineHero from "@/components/SkylineHero";

const Careers = () => {
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  const { t } = useLanguage();
  
  const jobOpenings = [
    // Accounting Department
    {
      title: "Senior AR Accountant",
      location: "Cairo Office",
      type: "Full-time",
      department: "Accounting",
      description: "Manage accounts receivable processes, oversee collections, and ensure accurate financial reporting for our hospitality portfolio."
    },
    {
      title: "General Accountant", 
      location: "Cairo Office",
      type: "Full-time",
      department: "Accounting",
      description: "Handle day-to-day accounting operations, financial record keeping, and support month-end closing procedures."
    },
    // Guest Relations Department
    {
      title: "Junior Guest Relations",
      location: "Cairo Office",
      type: "Full-time",
      department: "Guest Relations",
      description: "Provide exceptional guest service, handle inquiries and complaints, and ensure memorable experiences across our properties."
    },
    // Investment Department
    {
      title: "Investment Analyst",
      location: "Cairo Office", 
      type: "Full-time",
      department: "Investment",
      description: "Analyze investment opportunities, conduct market research, and support strategic decision-making for portfolio expansion."
    },
    // Business Department
    {
      title: "Senior Business Associate",
      location: "Cairo Office",
      type: "Full-time", 
      department: "Business",
      description: "Lead business development initiatives, manage client relationships, and drive strategic partnerships across the region."
    },
    {
      title: "Business Associate",
      location: "Cairo Office",
      type: "Full-time",
      department: "Business", 
      description: "Support business operations, assist with project management, and contribute to process improvement initiatives."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 z-10 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-3 py-2 border border-border/50"
        >
          <ArrowLeft className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={16} />
          <span className="text-xs sm:text-sm">{t("common.backToHome")}</span>
        </Link>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-[0.9] tracking-tight text-foreground">
                  Brassbell Careers
                </h1>
                <div className="w-8 sm:w-12 md:w-16 lg:w-20 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-foreground max-w-3xl mx-auto px-4 sm:px-0">
                Join our team of innovators, creators, and hospitality experts. At Brassbell, we're building the future of serviced living experiences across the MENA region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 right-10 w-40 h-40 border border-black/10 rounded-full"></div>
          <div className="absolute bottom-32 left-10 w-24 h-24 border border-black/5 rounded-full"></div>
          <div className="absolute top-1/3 left-1/3 w-64 h-64 border border-black/5 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Why Join Section */}
          <div className="max-w-6xl mx-auto mb-12 sm:mb-16 md:mb-20">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <div className="w-12 sm:w-16 md:w-20 h-1 bg-black mx-auto mb-4 sm:mb-6 md:mb-8"></div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-black mb-3 sm:mb-4 md:mb-6 px-4 sm:px-0">Why Join Brassbell?</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
                {t("careers.description")}
              </p>
            </div>

            {/* Benefits Cards - Mobile Optimized Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
              {/* Left Large Card */}
              <div className="lg:col-span-8">
                <div className="bg-black text-white p-6 sm:p-8 md:p-10 lg:p-12 rounded-2xl sm:rounded-3xl h-full relative overflow-hidden group">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-white/5 rounded-full blur-xl"></div>
                  
                  <div className="relative z-10">
                    <div className="w-12 sm:w-16 h-1 bg-white mb-4 sm:mb-6 md:mb-8"></div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mb-3 sm:mb-4 md:mb-6">Career Development</h3>
                    <p className="text-white/90 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6 md:mb-8">
                      Comprehensive training programs, mentorship opportunities, and clear career progression paths designed to accelerate your professional growth.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                      <div className="space-y-2 sm:space-y-3 md:space-y-4">
                        <div className="flex items-center">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-2 sm:mr-3 md:mr-4"></div>
                          <span className="text-white/90 text-xs sm:text-sm md:text-base">Mentorship Programs</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-2 sm:mr-3 md:mr-4"></div>
                          <span className="text-white/90 text-xs sm:text-sm md:text-base">Skills Development</span>
                        </div>
                      </div>
                      <div className="space-y-2 sm:space-y-3 md:space-y-4">
                        <div className="flex items-center">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-2 sm:mr-3 md:mr-4"></div>
                          <span className="text-white/90 text-xs sm:text-sm md:text-base">Leadership Training</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-2 sm:mr-3 md:mr-4"></div>
                          <span className="text-white/90 text-xs sm:text-sm md:text-base">Career Advancement</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Small Cards */}
              <div className="lg:col-span-4 space-y-4 sm:space-y-6 md:space-y-8">
                <div className="bg-white border-2 border-black p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl relative overflow-hidden group hover:shadow-xl transition-all duration-500">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-black/5 rounded-full blur-lg"></div>
                  <div className="relative z-10">
                    <div className="w-8 sm:w-10 md:w-12 h-1 bg-black mb-3 sm:mb-4 md:mb-6"></div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-medium mb-2 sm:mb-3 md:mb-4 text-black">Competitive Benefits</h3>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Competitive salary, health insurance, pension contributions, and employee discounts.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 border border-black/20 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl relative overflow-hidden group hover:shadow-xl transition-all duration-500">
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-black/5 rounded-full blur-lg"></div>
                  <div className="relative z-10">
                    <div className="w-8 sm:w-10 md:w-12 h-1 bg-black mb-3 sm:mb-4 md:mb-6"></div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-medium mb-2 sm:mb-3 md:mb-4 text-black">Work-Life Balance</h3>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Flexible working arrangements, generous holiday allowance, and wellness programs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white text-black relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-black/5 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full blur-xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <div className="w-12 sm:w-16 md:w-20 h-1 bg-black mx-auto mb-4 sm:mb-6 md:mb-8"></div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light mb-3 sm:mb-4 md:mb-6 px-4 sm:px-0">Current Opportunities</h2>
              <p className="text-base sm:text-lg md:text-xl text-black/90 max-w-2xl mx-auto px-4 sm:px-0">
                Join our team and help shape the future of hospitality
              </p>
            </div>
            
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              {jobOpenings.map((job, index) => (
                <div key={index} className="bg-black/5 backdrop-blur-sm border border-black/20 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl hover:bg-black/10 transition-all duration-500 group">
                  <div className="flex flex-col gap-4 sm:gap-6">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-light text-black">{job.title}</h3>
                        <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                          <span className="px-2 sm:px-3 py-1 bg-black/20 text-black text-xs sm:text-sm rounded-full">{job.location}</span>
                          <span className="px-2 sm:px-3 py-1 bg-black/20 text-black text-xs sm:text-sm rounded-full">{job.type}</span>
                          <span className="px-2 sm:px-3 py-1 bg-black/20 text-black text-xs sm:text-sm rounded-full">{job.department}</span>
                        </div>
                      </div>
                      <p className="text-black/80 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-0">{job.description}</p>
                    </div>
                    <div className="flex justify-start sm:justify-end">
                      <Button 
                        variant="black" 
                        size="sm"
                        onClick={() => setIsApplicationFormOpen(true)}
                        className="bg-black text-white hover:bg-black/90 transition-all duration-300 group-hover:scale-105 w-full sm:w-auto"
                      >
                        {t("careers.apply")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8 sm:mt-12 md:mt-16">
              <div className="bg-black/10 backdrop-blur-sm border border-black/20 p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-light mb-3 sm:mb-4 md:mb-6 text-black px-4 sm:px-0">Don't See Your Perfect Role?</h3>
                <p className="text-black/90 text-sm sm:text-base md:text-lg mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto px-4 sm:px-0">
                  We're always interested in hearing from talented individuals who share our passion for hospitality excellence.
                </p>
                <Button 
                  variant="black"
                  size="sm"
                  onClick={() => setIsApplicationFormOpen(true)}
                  className="bg-black text-white hover:bg-black/90 transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                >
                  Submit General Application
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
      <JobApplicationForm 
        open={isApplicationFormOpen} 
        onOpenChange={setIsApplicationFormOpen} 
      />
    </div>
  );
};

export default Careers;